import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  LinearProgress,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { type Dayjs } from 'dayjs';
import { useState } from 'react';
import {
  CalendarMonth,
  CalendarViewWeek,
  ChevronLeft,
  ChevronRight,
  FileDownload,
  TableView,
  Today,
  ViewList,
} from '@mui/icons-material';
import {
  CalendarDateRange,
  CalendarViewMode,
  ClassCalendarItem,
  ClassCalendarMetadata,
} from '../../types/ClassCalendar.type';
import type {
  WeeklyClassSheetDay,
  WeeklyClassSheetMetadata,
} from '../../types/WeeklyClassSheet.type';
import {
  addCalendarDays,
  dateFromKey,
  dateKeyFromDate,
  formatCalendarDate,
  formatCalendarTitle,
  formatWeekdayLabel,
  getCalendarDateKeys,
  getEventDateKey,
  isToday,
} from '../../utils/classCalendar';
import ClassCalendarEventCard, {
  ClassCalendarItemActions,
} from './ClassCalendarEventCard';
import WeeklyClassSheetPreview from './WeeklyClassSheetPreview';

interface ClassCalendarViewProps {
  view: CalendarViewMode;
  anchorDate: string;
  range: CalendarDateRange;
  items: ClassCalendarItem[];
  metadata: ClassCalendarMetadata | null;
  loading: boolean;
  error: string | null;
  onViewChange: (view: CalendarViewMode) => void;
  onToday: () => void;
  onDateChange: (dateKey: string) => void;
  onPrevious: () => void;
  onNext: () => void;
  onRetry: () => void;
  onSelectItem: (item: ClassCalendarItem) => void;
  onExportWeek?: () => void;
  exportLoading?: boolean;
  itemActions?: ClassCalendarItemActions;
  onBackToList?: () => void;
  sheetDays?: WeeklyClassSheetDay[];
  sheetMetadata?: WeeklyClassSheetMetadata | null;
  sheetLoading?: boolean;
  sheetError?: string | null;
  onSheetRetry?: () => void;
  sheetSubtype?: string;
  onSheetSubtypeChange?: (value: string) => void;
}

const ClassCalendarView = ({
  view,
  anchorDate,
  range,
  items,
  metadata,
  loading,
  error,
  onViewChange,
  onToday,
  onDateChange,
  onPrevious,
  onNext,
  onRetry,
  onSelectItem,
  onExportWeek,
  exportLoading = false,
  itemActions,
  onBackToList,
  sheetDays = [],
  sheetMetadata = null,
  sheetLoading = false,
  sheetError = null,
  onSheetRetry,
  sheetSubtype = 'All',
  onSheetSubtypeChange,
}: ClassCalendarViewProps) => {
  const [sheetView, setSheetView] = useState(false);
  const visibleDateKeys = getCalendarDateKeys(anchorDate, view);
  const todayKey = dateKeyFromDate(new Date());
  const currentMonthKey = anchorDate.slice(0, 7);
  const itemsByDate = new Map<string, ClassCalendarItem[]>();

  items.forEach((item) => {
    const dateKey = getEventDateKey(item);
    if (!dateKey) return;
    const dateItems = itemsByDate.get(dateKey) || [];
    dateItems.push(item);
    itemsByDate.set(dateKey, dateItems);
  });

  const renderToolbar = () => (
    <Box
      className={sheetView ? 'calendar-no-print' : undefined}
      sx={(theme) => ({
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1,
        flexWrap: 'wrap',
        px: { xs: 1, sm: 1.5 },
        py: 1.25,
        borderBottom: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.default,
        position: 'relative',
        zIndex: 10,
      })}
    >
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <Button
          size="small"
          variant="outlined"
          startIcon={<Today fontSize="small" />}
          onClick={onToday}
        >
          Today
        </Button>
        <IconButton
          size="small"
          aria-label="Previous period"
          onClick={onPrevious}
        >
          <ChevronLeft />
        </IconButton>
        <IconButton size="small" aria-label="Next period" onClick={onNext}>
          <ChevronRight />
        </IconButton>
        <Typography
          component="h2"
          variant="h6"
          sx={{ ml: 0.75, fontWeight: 700, color: 'text.primary' }}
        >
          {formatCalendarTitle(anchorDate, view)}
        </Typography>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            value={dayjs(anchorDate)}
            onChange={(nextDate: Dayjs | null) => {
              if (nextDate?.isValid()) {
                onDateChange(dateKeyFromDate(nextDate.toDate()));
              }
            }}
            format="DD/MM/YYYY"
            label="Select date"
            slotProps={{
              textField: {
                size: 'small',
                inputProps: { 'aria-label': 'Select calendar date' },
                sx: (theme) => ({
                  width: { xs: 142, sm: 154 },
                  '& .MuiInputBase-root': {
                    backgroundColor: theme.palette.background.paper,
                  },
                }),
              },
            }}
          />
        </LocalizationProvider>
      </Stack>

      <Stack direction="row" alignItems="center" spacing={1}>
        {onBackToList && (
          <Button
            size="small"
            variant="outlined"
            startIcon={<ViewList fontSize="small" />}
            onClick={onBackToList}
          >
            List View
          </Button>
        )}
        {onExportWeek && view === 'week' && (
          <Button
            size="small"
            variant="outlined"
            startIcon={
              exportLoading ? (
                <CircularProgress size={14} />
              ) : (
                <FileDownload fontSize="small" />
              )
            }
            onClick={onExportWeek}
            disabled={exportLoading}
          >
            {exportLoading ? 'Exporting…' : 'Export Current Week'}
          </Button>
        )}
        <ToggleButtonGroup
          size="small"
          exclusive
          value={sheetView ? 'sheet' : view}
          onChange={(_, nextView: CalendarViewMode | 'sheet' | null) => {
            if (!nextView) return;
            if (nextView === 'sheet') {
              onViewChange('week');
              setSheetView(true);
              return;
            }
            setSheetView(false);
            onViewChange(nextView);
          }}
          aria-label="calendar display mode"
        >
          <ToggleButton value="week" aria-label="Week view">
            <CalendarViewWeek fontSize="small" sx={{ mr: 0.5 }} />
            Week
          </ToggleButton>
          <ToggleButton value="month" aria-label="Month view">
            <CalendarMonth fontSize="small" sx={{ mr: 0.5 }} />
            Month
          </ToggleButton>
          <ToggleButton value="sheet" aria-label="Sheet view">
            <TableView fontSize="small" sx={{ mr: 0.5 }} />
            Sheet
          </ToggleButton>
        </ToggleButtonGroup>
      </Stack>
    </Box>
  );

  const renderNotice = () => {
    const activeLoading = sheetView ? sheetLoading : loading;
    const activeError = sheetView ? sheetError : error;
    const activeMetadata = sheetView ? sheetMetadata : metadata;

    return (
      <Stack spacing={1} sx={{ px: { xs: 1, sm: 1.5 }, pt: 1.25 }}>
        {activeLoading && <LinearProgress aria-label="Loading calendar" />}
        {activeError && !sheetView && (
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={onRetry}>
                Retry
              </Button>
            }
          >
            Unable to load class calendar.
          </Alert>
        )}
        {activeMetadata && activeMetadata.unscheduledItems > 0 && (
          <Alert severity="info">
            {activeMetadata.unscheduledItems} class(es) have no date and are not
            shown on the calendar.
          </Alert>
        )}
      </Stack>
    );
  };

  const renderWeek = () => {
    const weekDateKeys = visibleDateKeys.slice(0, 7);
    const getSortedItems = (dateKey: string) =>
      [...(itemsByDate.get(dateKey) || [])].sort((left, right) => {
        const leftTime = left.startTime || '99:99';
        const rightTime = right.startTime || '99:99';
        return (
          leftTime.localeCompare(rightTime) ||
          left.name.localeCompare(right.name) ||
          left.id - right.id
        );
      });

    return (
      <Box sx={{ px: { xs: 0, sm: 1.5 }, pb: 1.5, pt: 1 }}>
        <Box
          sx={(theme) => ({
            minWidth: { xs: 1050, md: 0 },
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 1.5,
            overflow: 'visible',
            backgroundColor: theme.palette.background.paper,
            display: 'grid',
            gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
          })}
        >
          {weekDateKeys.map((dateKey) => (
            <Box
              key={`header-${dateKey}`}
              sx={(theme) => ({
                px: 1,
                py: 1,
                textAlign: 'center',
                backgroundColor: isToday(dateKey)
                  ? alpha(theme.palette.primary.main, 0.14)
                  : theme.palette.background.default,
                borderBottom: `1px solid ${theme.palette.divider}`,
                borderRight: `1px solid ${theme.palette.divider}`,
                position: 'sticky',
                top: 0,
                zIndex: 5,
              })}
              aria-current={dateKey === todayKey ? 'date' : undefined}
            >
              <Typography
                variant="caption"
                sx={{ display: 'block', fontWeight: 700 }}
              >
                {formatWeekdayLabel(dateKey)}
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontWeight: 700,
                  color: isToday(dateKey) ? '#1565c0' : '#26364a',
                }}
              >
                {dateFromKey(dateKey).getDate()}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {formatCalendarDate(dateKey)}
              </Typography>
            </Box>
          ))}
          {weekDateKeys.map((dateKey) => {
            const dateItems = getSortedItems(dateKey);
            return (
              <Box
                key={`body-${dateKey}`}
                sx={(theme) => ({
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.6,
                  minHeight: { xs: 520, md: 620 },
                  p: 0.75,
                  borderRight: `1px solid ${theme.palette.divider}`,
                  borderBottom: `1px solid ${theme.palette.divider}`,
                  backgroundColor: isToday(dateKey)
                    ? theme.palette.background.default
                    : theme.palette.background.paper,
                })}
              >
                {dateItems.map((item) => (
                  <ClassCalendarEventCard
                    key={item.id}
                    item={item}
                    actions={itemActions}
                    dense
                    showStudents
                    onClick={onSelectItem}
                  />
                ))}
              </Box>
            );
          })}
        </Box>
      </Box>
    );
  };

  const renderMonth = () => (
    <Box sx={{ px: { xs: 0, sm: 1.5 }, pb: 1.5, pt: 1 }}>
      <Box
        sx={(theme) => ({
          minWidth: { xs: 860, md: 0 },
          display: 'grid',
          gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
          borderTop: `1px solid ${theme.palette.divider}`,
          borderLeft: `1px solid ${theme.palette.divider}`,
          borderRadius: 1.5,
          overflow: 'visible',
          backgroundColor: theme.palette.background.paper,
        })}
      >
        {visibleDateKeys.slice(0, 7).map((dateKey) => (
          <Box
            key={dateKey}
            sx={(theme) => ({
              px: 1,
              py: 1,
              textAlign: 'center',
              fontSize: 12,
              fontWeight: 700,
              color: 'text.secondary',
              backgroundColor: theme.palette.background.default,
              borderRight: `1px solid ${theme.palette.divider}`,
              borderBottom: `1px solid ${theme.palette.divider}`,
              position: 'sticky',
              top: 0,
              zIndex: 5,
            })}
          >
            {formatWeekdayLabel(dateKey)}
          </Box>
        ))}
        {visibleDateKeys.map((dateKey) => {
          const dateItems = itemsByDate.get(dateKey) || [];
          const isCurrentMonth = dateKey.slice(0, 7) === currentMonthKey;
          return (
            <Box
              key={dateKey}
              sx={(theme) => ({
                display: 'flex',
                flexDirection: 'column',
                gap: 0.6,
                minHeight: { xs: 190, md: 170 },
                p: 0.75,
                borderRight: `1px solid ${theme.palette.divider}`,
                borderBottom: `1px solid ${theme.palette.divider}`,
                backgroundColor: isToday(dateKey)
                  ? alpha(theme.palette.primary.main, 0.14)
                  : isCurrentMonth
                    ? theme.palette.background.paper
                    : theme.palette.background.default,
              })}
              aria-label={`Calendar date ${formatCalendarDate(dateKey)}`}
            >
              <Typography
                variant="body2"
                sx={{
                  alignSelf: 'flex-start',
                  minWidth: 26,
                  px: 0.6,
                  py: 0.25,
                  borderRadius: 10,
                  textAlign: 'center',
                  fontWeight: 700,
                  color: isToday(dateKey)
                    ? '#fff'
                    : isCurrentMonth
                      ? '#26364a'
                      : '#9aa6b5',
                  backgroundColor: isToday(dateKey) ? '#1976d2' : 'transparent',
                }}
              >
                {dateFromKey(dateKey).getDate()}
              </Typography>
              <Stack spacing={0.6} sx={{ minWidth: 0 }}>
                {dateItems.map((item) => (
                  <ClassCalendarEventCard
                    key={item.id}
                    item={item}
                    actions={itemActions}
                    dense
                    onClick={onSelectItem}
                  />
                ))}
              </Stack>
            </Box>
          );
        })}
      </Box>
    </Box>
  );

  return (
    <Box
      className={
        sheetView ? 'calendar-shell calendar-shell-sheet' : 'calendar-shell'
      }
      component="section"
      aria-label="Class calendar"
      sx={(theme) => ({
        display: 'flex',
        flexDirection: 'column',
        minHeight: { xs: 440, md: 520 },
        maxHeight: sheetView
          ? 'none'
          : { xs: 'calc(100dvh - 180px)', md: 'calc(100vh - 220px)' },
        overflow: sheetView ? 'visible' : 'hidden',
        borderTop: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
      })}
    >
      {renderToolbar()}
      {renderNotice()}
      <Box
        sx={(theme) => ({
          flex: 1,
          minHeight: 0,
          overflowY: sheetView ? 'visible' : 'scroll',
          overflowX: sheetView ? 'visible' : 'auto',
          scrollbarGutter: 'stable',
          overscrollBehavior: 'contain',
          scrollbarColor: `${theme.palette.grey[400]} ${theme.palette.action.hover}`,
          '&::-webkit-scrollbar': {
            width: 10,
            height: 10,
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: theme.palette.grey[400],
            borderRadius: 5,
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: theme.palette.action.hover,
          },
        })}
      >
        {sheetView ? (
          <WeeklyClassSheetPreview
            days={sheetDays}
            metadata={sheetMetadata}
            loading={sheetLoading}
            error={sheetError}
            subtype={sheetSubtype}
            onSubtypeChange={onSheetSubtypeChange}
            onRetry={onSheetRetry || onRetry}
          />
        ) : loading && items.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress aria-label="Loading class calendar" />
          </Box>
        ) : view === 'week' ? (
          renderWeek()
        ) : (
          renderMonth()
        )}
      </Box>
      <Typography
        className="calendar-no-print"
        variant="caption"
        color="text.secondary"
        sx={{ display: 'block', px: { xs: 1, sm: 1.5 }, pb: 1.25 }}
      >
        Showing {range.from} to{' '}
        {dateKeyFromDate(addCalendarDays(dateFromKey(range.toExclusive), -1))} (
        {items.length} class(es))
      </Typography>
    </Box>
  );
};

export default ClassCalendarView;
